// File: frontend/src/pages/ManagePreferences/ManagePreferences.js

import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useNewsletter } from '../../hooks/useNewsletter';
import Button from '../../components/Button/Button';
import Alert from '../../components/Alert/Alert';
import { FiSettings, FiMail, FiAlertTriangle } from 'react-icons/fi';
import styles from './ManagePreferences.module.css';

const ManagePreferences = () => {
  const { t } = useLanguage();
  const { unsubscribe, verifyPreferencesToken, isLoading, error, clearError } = useNewsletter();
  const location = useLocation();
  const navigate = useNavigate();
  
  const [emailAddress, setEmailAddress] = useState('');
  const [isConfirmingUnsubscribe, setIsConfirmingUnsubscribe] = useState(false);
  const [isValidToken, setIsValidToken] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);
  const [userData, setUserData] = useState({});
  
  // Add a ref to track if verification has been attempted
  const verificationAttempted = useRef(false);
  
  // Parse email and token from URL query parameters and verify token
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const email = params.get('email');
    const token = params.get('token');
    
    // Only proceed if we have both email and token and haven't attempted verification yet
    if (email && token && !verificationAttempted.current) {
      const decodedEmail = decodeURIComponent(email);
      setEmailAddress(decodedEmail);

      // Mark that we've attempted verification
      verificationAttempted.current = true;
      
      // Verify token with backend
      verifyPreferencesToken(token, decodedEmail)
        .then(response => {
          setIsValidToken(true);
          setUserData({
            firstName: response.firstName || '',
            lastName: response.lastName || '',
            status: response.status
          });
        })
        .catch(err => {
          setIsValidToken(false);
          // Error is handled by the hook
        })
        .finally(() => {
          setIsVerifying(false);
        });
    } else if (!email || !token) {
      // If no email or token in URL, redirect to home page
      navigate('/');
    }
  }, [location.search, navigate, verifyPreferencesToken]); // Only depend on location.search, not the entire location object
  
  const handleUnsubscribeClick = () => {
    setIsConfirmingUnsubscribe(true);
  };
  
  const handleConfirmUnsubscribe = async () => {
    try {
      await unsubscribe({ emailAddress });
      // Redirect to unsubscribe confirmation page
      navigate(`/unsubscribe?email=${encodeURIComponent(emailAddress)}&success=true`);
    } catch (err) {
      // Error handling is done in the hook
    }
  };
  
  const handleCancelUnsubscribe = () => {
    setIsConfirmingUnsubscribe(false);
  };

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.preferencesContainer}>
        <div className={styles.heroSection}>
          <h1 className={styles.title}>
            {t('newsletter.preferences.title', 'Manage Newsletter Preferences')}
          </h1>
          <div className={styles.titleUnderline}></div>
        </div>

        <div className={styles.contentSection}>
          {error && (
            <Alert 
              type="error" 
              titleKey="newsletter.error.title"
              messageKey={`newsletter.errors.${error}`}
              onClose={clearError}
            />
          )}
          
          {isVerifying ? (
            <div className={styles.loadingContainer}>
              <p>{t('newsletter.preferences.verifying', 'Verifying your access...')}</p>
              {/* Add a loading spinner here if desired */}
            </div>
          ) : isValidToken ? (
            isConfirmingUnsubscribe ? (
              <div className={styles.confirmContainer}>
                <div className={styles.confirmIcon}>
                  <FiAlertTriangle size={40} />
                </div>
                <h2 className={styles.confirmTitle}>
                  {t('newsletter.preferences.confirm_unsub_title', 'Confirm Unsubscription')}
                </h2>
                <p className={styles.confirmMessage}>
                  {t('newsletter.preferences.confirm_unsub_message', 'Are you sure you want to unsubscribe from our newsletter? You will no longer receive updates from us.')}
                </p>
                <div className={styles.emailDisplay}>
                  <FiMail className={styles.emailIcon} />
                  <span className={styles.emailText}>{emailAddress}</span>
                </div>
                
                <div className={styles.actionButtons}>
                  <Button 
                    variant="primary" 
                    size="medium" 
                    onClick={handleConfirmUnsubscribe}
                    isLoading={isLoading}
                  >
                    {t('newsletter.preferences.confirm_unsub_button', 'Confirm Unsubscribe')}
                  </Button>
                  <Button 
                    variant="secondary" 
                    size="medium" 
                    onClick={handleCancelUnsubscribe}
                  >
                    {t('newsletter.preferences.cancel_button', 'Cancel')}
                  </Button>
                </div>
              </div>
            ) : (
              <div className={styles.preferencesPanel}>
                <div className={styles.panelHeader}>
                  <FiSettings className={styles.panelIcon} />
                  <h2 className={styles.panelTitle}>
                    {t('newsletter.preferences.panel_title', 'Your Newsletter Preferences')}
                  </h2>
                </div>
                
                <div className={styles.emailSection}>
                  <h3 className={styles.sectionTitle}>
                    {t('newsletter.preferences.email_section', 'Subscription Email')}
                  </h3>
                  <div className={styles.emailDisplay}>
                    <FiMail className={styles.emailIcon} />
                    <span className={styles.emailText}>{emailAddress}</span>
                  </div>
                </div>
                
                <div className={styles.optionsSection}>
                  <h3 className={styles.sectionTitle}>
                    {t('newsletter.preferences.options_section', 'Subscription Options')}
                  </h3>
                  <p className={styles.optionsDescription}>
                    {t('newsletter.preferences.options_description', 'You are currently subscribed to our newsletter. You can unsubscribe at any time.')}
                  </p>
                  
                  <div className={styles.optionCard}>
                    <div className={styles.optionInfo}>
                      <h4 className={styles.optionTitle}>
                        {t('newsletter.preferences.general_updates', 'General Updates')}
                      </h4>
                      <p className={styles.optionDescription}>
                        {t('newsletter.preferences.general_description', 'Product updates, news, and insights from Allegory')}
                      </p>
                    </div>
                    <div className={styles.optionStatus}>
                      <span className={styles.statusBadge}>
                        {t('newsletter.preferences.subscribed', 'Subscribed')}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className={styles.actionsSection}>
                  <h3 className={styles.sectionTitle}>
                    {t('newsletter.preferences.actions_section', 'Subscription Actions')}
                  </h3>
                  <Button 
                    variant="secondary" 
                    size="medium" 
                    onClick={handleUnsubscribeClick}
                    className={styles.unsubscribeButton}
                  >
                    {t('newsletter.preferences.unsubscribe_button', 'Unsubscribe from all emails')}
                  </Button>
                </div>
                
                <div className={styles.infoSection}>
                  <p className={styles.infoText}>
                    {t('newsletter.preferences.info_text', 'We respect your privacy and are committed to protecting your personal data. For more information, please see our ')}
                    <a href="/privacy-policy" className={styles.inlineLink}>
                      {t('newsletter.preferences.privacy_policy', 'Privacy Policy')}
                    </a>.
                  </p>
                </div>
              </div>
            )
          ) : (
            <div className={styles.errorContainer}>
              <div className={styles.errorIcon}>
                <FiAlertTriangle size={40} />
              </div>
              <h2 className={styles.errorTitle}>
                {t('newsletter.preferences.invalid_token_title', 'Invalid or Expired Link')}
              </h2>
              <p className={styles.errorMessage}>
                {t('newsletter.preferences.invalid_token_message', 'The link you used is invalid or has expired. Please request a new link or contact support.')}
              </p>
              <Button 
                variant="primary" 
                size="medium" 
                onClick={() => navigate('/')}
              >
                {t('newsletter.preferences.go_home', 'Go to Homepage')}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManagePreferences;